import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import WrapperPage from "../../components/WrapperPage";
import useViewController from "./viewController";
import useCurrentUserData from "../../hooks/useCurrentUserData";
import BaseTextButton from "../../components/BaseTextButton";
import BaseButton from "../../components/BaseButton";
import PersonalAccount from "../../store/PersonalAccount";
import Game from "../../store/Game";
import useController from "./controller";
import BaseSpinner from "../../components/BaseSpinner";
import "./style.scss";
import InsightFormProtect from "../../hocs/InsightFormProtect";
import { observer } from "mobx-react-lite";
import config from "../../config";
import useCurrentGame from "../../hooks/useCurrentGame";
import useModalActions from "../../hooks/useModalActions";
import { MODAL_TYPES } from "../../utils/emitter";
import AnketaDescriptionBody from "../../components/AnketaDescriptionBody";
import BuyMoneyPointsRow from "../../components/BuyMoneyPointsRow";

// observer должен оборачивать САМ компонент, а не HOC: InsightFormProtect рендерит
// <Component/> отдельным элементом, поэтому observer поверх него отслеживал только
// пустой рендер обёртки. Пока список карточек жил в useState, это не мешало, но
// 21.09 экран начал читать Game.zones — и перестал перерисовываться, когда зоны
// доезжали: юзер залипал на спиннере.
export default InsightFormProtect(
  observer(function InsightPickCards() {
    const { t } = useTranslation();
    const {
      cards,
      isDemoGame,
      hasZoneCards,
      zonesReady,
      zonesLoadFailed,
      retryZones,
      showMore,
      showLess,
    } = useViewController();
    const { createInsightForm, creatingCard } = useController();
    const { getUserData } = useCurrentUserData();
    const { getCurrentGame, setGame } = useCurrentGame();
    const { openModal } = useModalActions();
    const userData = getUserData();

    const isCanShow = useMemo(() => cards.some((card) => card.isHide), [cards]);

    // item 17.09: кнопка «Создать свою тему» убрана отсюда — она переехала на экран
    // «Анкета Откровений» («Создать тему»). Здесь у НЕ купившего игру любой клик по
    // карточке и «Показать все карточки» открывают попап с описанием Анкеты.
    // покупка за баллы проходит без перезагрузки: обновляем игру, иначе экран
    // остаётся в демо-состоянии и кнопки продолжают предлагать купить
    const onPurchased = async () => {
      const id = getCurrentGame()?.id;
      // BuyMoneyPointsRow уже дёрнул getPersonalAccountData, там лежит свежая
      // игра с type PREMIUM — переносим её в стор и localStorage, иначе экран
      // остаётся демо-шным и продолжает предлагать купить уже купленное
      const fresh = (PersonalAccount.games || []).find((g) => g.id === id);
      if (fresh) {
        Game.setGame(fresh);
        setGame(fresh);
      }
      retryZones();
    };

    const openAnketaPopup = () => {
      // 21.09 (решение клиента): попап с ПОЛНЫМ описанием анкеты — тем же текстом,
      // что и страница, минус блок «Инструкция» — и кнопками деньги/баллы внизу.
      openModal(MODAL_TYPES.BUY_PRODUCT, {
        productType: "GAME",
        variant: "anketa_full",
        gameId: getCurrentGame()?.id,
      });
    };

    // 21.09: карточки сюда приходят из «Игры». Пока их там нет (тест раскидан не
    // обоими — бэк не отдаёт зоны), показываем не пустую сетку, а то же описание
    // анкеты страницей. Купившему полную игру продавать нечего — ему вместо
    // кнопок покупки объясняем, чего ждать.
    // ждём именно ЗАГРУЗКИ зон, а не «флаг загрузки выключен»: он стартует как
    // false, и на первом кадре экран описания мелькал даже у того, у кого карточки есть
    if (!zonesReady) {
      return (
        <div className="insight_pick_cards_page">
          <WrapperPage
            headerTitle={t("insight_pick_cards.title")}
            backBtn
            burgerMenu
          >
            <div className="insight_pick_cards_page__loader">
              <BaseSpinner />
            </div>
          </WrapperPage>
        </div>
      );
    }

    // 24.09: сбой загрузки зон — это НЕ «карточек ещё нет». Раньше сеть отвалилась
    // бы, и купивший читал бы «карточки появятся, когда вы оба пройдёте тест».
    if (zonesLoadFailed) {
      return (
        <div className="insight_pick_cards_page">
          <WrapperPage
            headerTitle={t("insight_pick_cards.title")}
            backBtn
            burgerMenu
          >
            <div className="insight_pick_cards_page__waiting">
              {t("insight_pick_cards.load_error")}
            </div>
            <div className="insight_pick_cards_page__retry">
              <BaseButton unfilled onClick={retryZones}>
                {t("insight_pick_cards.load_retry")}
              </BaseButton>
            </div>
          </WrapperPage>
        </div>
      );
    }

    if (!hasZoneCards) {
      return (
        <div className="insight_pick_cards_page insight_pick_cards_page--description">
          <WrapperPage
            headerTitle={t("insight_form_new_description_page.title")}
            backBtn
            burgerMenu
          >
            <AnketaDescriptionBody />
            {isDemoGame ? (
              <BuyMoneyPointsRow
                productType="GAME"
                gameId={getCurrentGame()?.id}
                onPurchased={onPurchased}
                className="insight-form-new-description-page__buy-row"
              />
            ) : (
              <div className="insight_pick_cards_page__waiting">
                {t("insight_pick_cards.waiting_cards")}
              </div>
            )}
          </WrapperPage>
        </div>
      );
    }

    return (
      <div className="insight_pick_cards_page">
        <WrapperPage
          headerTitle={t("insight_pick_cards.title")}
          backBtn
          burgerMenu
        >
          <div className="insight_pick_cards_page__desc">
            {t("insight_pick_cards.desc")}
          </div>
          {cards && cards.length ? (
            <div className="insight_pick_cards_page__cards">
              {cards
                .filter((card) => !card.isHide)
                .map((card, idx) => {
                  return (
                    <div
                      onClick={() => {
                        if (isDemoGame) return openAnketaPopup();
                        return creatingCard ? null : createInsightForm(card);
                      }}
                      className="insight_pick_cards_page__card"
                      key={card.id}
                    >
                      {creatingCard && creatingCard.id === card.id ? (
                        <div className="insight_pick_cards_page__card--spinner">
                          <BaseSpinner />
                        </div>
                      ) : null}

                      <div
                        style={{
                          opacity:
                            creatingCard && creatingCard?.id === card.id
                              ? 0.5
                              : 1,
                        }}
                      >
                        {card?.cardUrl && (
                          <img
                            src={`${config.staticApiUrl}${card.cardUrl}`}
                            alt={card.name}
                          />
                        )}
                      </div>
                      {userData && (
                        <div className="insight_pick_cards_page__card--name">
                          {userData.gender === "male"
                            ? card.question_for_male
                            : card.question_for_female}
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          ) : (
            <div className="insight_pick_cards_page__loader">
              <BaseSpinner />
            </div>
          )}
          <div className="insight_pick_cards_page__toggle-button">
            <BaseTextButton
              onClick={() => {
                if (isDemoGame) return openAnketaPopup();
                return isCanShow ? showMore() : showLess();
              }}
            >
              {/* у некупленной игры скрывать нечего — видны все карточки,
                  и кнопка работает как триггер попапа, поэтому подпись постоянная */}
              {isDemoGame || isCanShow
                ? t("insight_pick_cards.showCards")
                : t("insight_pick_cards.hideCards")}
            </BaseTextButton>
          </div>
        </WrapperPage>
      </div>
    );
  })
);
